import React from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, Text, Group, Box, Anchor, Stack, Flex } from '@mantine/core';
import {
    IconFileSearch,
    IconExternalLink,
    IconCalendarDue,
    IconUser,
} from '@tabler/icons-react';

// 노트 검색 결과 타입
interface NoteResult {
    id?: string;
    title?: string;
    date?: string;
    url?: string;
    author_name?: string; // 작성자 정보 추가
}

interface SearchNoteResultProps {
    results: NoteResult[];
}

/**
 * 노트 검색 결과를 표시하는 컴포넌트
 */
const SearchNoteResult: React.FC<SearchNoteResultProps> = ({ results }) => {
    const { t, i18n } = useTranslation('ai');
    // 결과 데이터 로깅
    console.log('[SearchNoteResult] 받은 노트 검색 결과:', results);
    console.log(
        '[SearchNoteResult] 첫 번째 결과 작성자 정보:',
        results[0]?.author_name
    );

    if (!results || results.length === 0) {
        return null;
    }

    // 날짜 형식화 유틸리티 함수
    const formatDate = (dateString: string | null | undefined) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString(i18n.language, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'short',
        });
    };

    return (
        <Paper
            p={0}
            radius="md"
            shadow="none"
            style={{
                width: '445px',
                overflow: 'hidden',
                background:
                    'linear-gradient(126.65deg, rgba(47, 146, 253, 0.1) -12.41%, rgba(102, 217, 232, 0.1) 84.63%), #FFFFFF',
                borderRadius: '8px',
            }}
        >
            {/* 헤더 부분 */}
            <Flex direction="column" p="md">
                <Flex align="center" mb={16} style={{ width: '100%' }}>
                    <Flex align="center">
                        <IconFileSearch size={20} color="#228BE6" />
                        <Text
                            fw={600}
                            size="sm"
                            ml={8}
                            style={{ color: '#343A40' }}
                        >
                            {t('searchNote.results')}
                        </Text>
                    </Flex>

                    <Text size="sm" ml="auto" style={{ color: '#868E96' }}>
                        {t('searchNote.itemsFound', { count: results.length })}
                    </Text>
                </Flex>

                {/* 내용 부분 */}
                <Box
                    style={{
                        borderRadius: '16px',
                        marginTop: '-6px',
                        width: '100%',
                        boxSizing: 'border-box',
                    }}
                >
                    <Stack gap="md">
                        {results.map((note, index) => (
                            <Paper
                                key={note.id || index}
                                p="md"
                                style={{
                                    backgroundColor: 'white',
                                    borderRadius: '8px',
                                }}
                            >
                                <Box mb={4}>
                                    <Text
                                        fw={600}
                                        size="sm"
                                        style={{ color: '#333333' }}
                                    >
                                        {note.title || t('searchNote.noTitle')}
                                    </Text>
                                </Box>
                                <Flex
                                    align="center"
                                    justify="space-between"
                                    mt={8}
                                >
                                    <Flex>
                                        {note.author_name && (
                                            <Flex align="center" mr={16}>
                                                <IconUser
                                                    size={16}
                                                    color="#868E96"
                                                    style={{
                                                        marginRight: '6px',
                                                    }}
                                                />
                                                <Text size="xs" color="#868E96">
                                                    {note.author_name ||
                                                        t(
                                                            'searchNote.noAuthor'
                                                        )}
                                                </Text>
                                            </Flex>
                                        )}
                                        {note.date && (
                                            <Flex align="center">
                                                <IconCalendarDue
                                                    size={16}
                                                    color="#868E96"
                                                    style={{
                                                        marginRight: '6px',
                                                    }}
                                                />
                                                <Text size="xs" color="#868E96">
                                                    {formatDate(note.date)}
                                                </Text>
                                            </Flex>
                                        )}
                                    </Flex>
                                    <Anchor
                                        href={note.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        size="sm"
                                    >
                                        <Group gap={4}>
                                            <Text size="xs" c="#228BE6">
                                                {t('searchNote.viewNote')}
                                            </Text>
                                            <IconExternalLink
                                                size={16}
                                                color="#228BE6"
                                            />
                                        </Group>
                                    </Anchor>
                                </Flex>
                            </Paper>
                        ))}
                    </Stack>
                </Box>
            </Flex>
        </Paper>
    );
};

export default SearchNoteResult;
